import { Controller, useFormContext, UseControllerProps } from 'react-hook-form';
import { NumberField } from '@schuettflix/planum-react';
import { cn } from '@/shared/utils/cn.ts';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';
import { SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { CurrencyCode } from '@schuettflix/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { TaxClassOptions } from '@/clients/tax-classes/useTaxClasses.ts';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { parseNumberTo2Decimals } from '@/shared/utils/parseNumberTo2Decimals.ts';

type PriceFieldValues = {
    [key: string]: number;
};

type TaxClassFieldValues = {
    [key: string]: string;
};

type PriceProps = Pick<UseControllerProps<PriceFieldValues>, 'name' | 'defaultValue'> & {
    placeholder?: string;
    disabled?: boolean;
    unit?: SUPPORTED_UNITS;
    currencyCode?: CurrencyCode;
    testId?: string;
    onPriceChange?: (value?: number) => void;
};

type TaxClassProps = Pick<UseControllerProps<TaxClassFieldValues>, 'name' | 'defaultValue'> & {
    disabled?: boolean;
    onTaxClassChange?: (taxClass: string | null) => void;
    testId?: string;
    options: TaxClassOptions[];
};

export type PriceAndTaxClassProps = {
    priceProps: PriceProps;
    taxClassProps: TaxClassProps;
    className?: string;
};

export const PRICE_DEFAULT_NAME = 'price';
export const TAX_CLASS_DEFAULT_NAME = 'taxClass';

export const PriceAndTaxClassInput: React.FC<PriceAndTaxClassProps> = React.forwardRef<
    HTMLInputElement,
    PriceAndTaxClassProps
>(({ priceProps, taxClassProps, className }, _ref) => {
    const { control } = useFormContext();
    const { t } = useTranslation();
    const { getCurrencySymbol } = useFormatters();

    return (
        <div className={cn('grid grid-cols-3 items-start gap-2', className)}>
            <Controller
                name={priceProps.name || PRICE_DEFAULT_NAME}
                defaultValue={priceProps.defaultValue ?? null}
                control={control}
                render={({ field: { onChange, value, ...field }, fieldState }) => (
                    <NumberField
                        {...field}
                        value={typeof value === 'number' ? value : undefined}
                        size="lg"
                        className="col-span-2 w-full"
                        trailingSlot={
                            <span>{`${getCurrencySymbol(priceProps.currencyCode)}${
                                priceProps.unit ? ' / ' + t(SUPPORTED_UNIT_LABEL[priceProps.unit]) : ''
                            }`}</span>
                        }
                        onValueChange={value => {
                            const parsedValue = typeof value === 'number' ? parseNumberTo2Decimals(value) : undefined;
                            const currentValue = parsedValue ? Number(parsedValue) : undefined;

                            priceProps.onPriceChange?.(currentValue);
                            onChange(currentValue);
                        }}
                        minimumFractionDigits={2}
                        step={0.01}
                        disabled={priceProps.disabled}
                        error={fieldState.error?.message}
                        placeholder={priceProps.placeholder ?? t('forms.inputs.PriceAndTaxClassInput.price')}
                        aria-label={priceProps.placeholder ?? t('forms.inputs.PriceAndTaxClassInput.price')}
                        data-test={priceProps.testId}
                    />
                )}
            />
            <Controller
                name={taxClassProps.name || TAX_CLASS_DEFAULT_NAME}
                defaultValue={taxClassProps.defaultValue ?? null}
                control={control}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SelectCombobox
                        fieldRef={ref}
                        value={value ?? undefined}
                        onValueChange={taxClass => {
                            taxClassProps.onTaxClassChange?.(taxClass);
                            onChange(taxClass);
                        }}
                        options={taxClassProps.options.map(taxClassOption => ({
                            value: taxClassOption.value,
                            label: taxClassOption.label,
                        }))}
                        disabled={taxClassProps.disabled}
                        placeholder={t('product.quote.tax')}
                        error={fieldState.error?.message}
                        size="lg"
                        dataTest={taxClassProps.testId}
                        className="col-span-1"
                    />
                )}
            />
        </div>
    );
});
